import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import type { SlotCode } from '../lib/types'

const ROSTER_PRESETS: { label: string; slots: SlotCode[] }[] = [
  { label: 'Standard six (QB/RB/WR/TE/K/DEF)', slots: ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'] },
  { label: 'Skill only (QB/RB/WR/TE)', slots: ['QB', 'RB', 'WR', 'TE'] },
  { label: 'Six + flex', slots: ['QB', 'RB', 'WR', 'TE', 'K', 'DEF', 'FLEX'] },
]

/**
 * Burn pressure by season length, measured on 2024-25 (sim/short_season.py).
 * "Reach" is how far down the board you're picking late in the season; 1.0
 * means no pressure at all. Below ~2.8 the burn stops driving decisions.
 */
function seasonQuality(weeks: number): { label: string; tone: string; note: string } {
  if (weeks >= 14) return {
    label: 'Full strength', tone: 'good',
    note: `${weeks} weeks — you'll spend ${weeks * 6} of 192 units. The board runs dry by the end, which is the whole game.`,
  }
  if (weeks >= 10) return {
    label: 'Works well', tone: 'good',
    note: `${weeks} weeks — ${weeks * 6} units spent. Real pressure late, a little softer than a full season.`,
  }
  if (weeks >= 7) return {
    label: 'Thin', tone: 'warn',
    note: `${weeks} weeks — only ${weeks * 6} of 192 units. You'll still hold near-top options at the end, so the burn barely bites. Playable, but closer to weekly fantasy.`,
  }
  return {
    label: 'Too short', tone: 'warn',
    note: `${weeks} weeks is not really this game — almost nothing gets burned. Good for a demo, not a season.`,
  }
}

export default function CreateLeague() {
  const nav = useNavigate()
  const [startWeek, setStartWeek] = useState(1)
  const [endWeek, setEndWeek] = useState(18)
  const [joinDeadline, setJoinDeadline] = useState<number | ''>('')
  const [isPublic, setIsPublic] = useState(false)
  const [maxMembers, setMaxMembers] = useState(12)
  const [name, setName] = useState('')
  const [teamName, setTeamName] = useState('')
  const [preset, setPreset] = useState(0)
  const [profileId, setProfileId] = useState('half_ppr')
  const [hidePicks, setHidePicks] = useState(true)
  const [playoffReset, setPlayoffReset] = useState(true)
  const [universe, setUniverse] = useState<'nfl' | 'fbs'>('nfl')
  const [conference, setConference] = useState('')
  const [confs, setConfs] = useState<{ conference: string; teams: number; rooms: number }[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Conferences come from the season-keyed table rather than a hardcoded
  // list, because FBS realigns most years and a stale array here would offer
  // a conference that no longer has the teams in it.
  useEffect(() => {
    void (async () => {
      const { data } = await supabase.rpc('list_conferences', { p_season: 2026 })
      const rows = (data ?? []) as { conference: string; teams: number; rooms: number }[]
      setConfs(rows)
      if (rows.length && !conference) setConference(rows[0].conference)
    })()
  }, [])

  // The same arithmetic the database enforces, shown before you submit rather
  // than as an error afterwards. Pressure is the fraction of the board a
  // season demands: the NFL game sits at 0.56, over 1.0 cannot be finished.
  const slotCount = ROSTER_PRESETS[preset].slots.length
  const weekCount = Math.max(1, endWeek - startWeek + 1)
  const rooms = universe === 'nfl'
    ? 192
    : (confs.find((c) => c.conference === conference)?.rooms ?? 0)
  const pressure = rooms ? (slotCount * weekCount) / rooms : null

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setErr(null)

    // One call, one transaction. Creating the league and joining it used to be
    // two round trips, which failed for a subtle reason: .insert().select() is
    // INSERT ... RETURNING, Postgres applies SELECT policies to the returned
    // row, and a private league with no members yet is unreadable even by the
    // person who just made it. It also left orphan leagues behind whenever the
    // second call failed.
    const { data, error } = await supabase.rpc('create_league', {
      p_name: name,
      p_team_name: teamName,
      p_roster: ROSTER_PRESETS[preset].slots,
      p_profile: profileId,
      p_start_week: startWeek,
      p_end_week: endWeek,
      p_join_deadline: joinDeadline === '' ? null : Number(joinDeadline),
      p_hide_picks: hidePicks,
      p_playoff_reset: playoffReset,
      p_public: isPublic,
      p_max_members: maxMembers,
      p_universe: universe,
      p_conference: universe === 'fbs' ? conference : null,
    })

    setBusy(false)
    if (error || !data) return setErr(error?.message ?? 'Could not create league')

    nav(`/app/l/${(data as { id: string }).id}`)
  }

  return (
    <div className="page">
      <header className="topbar">
        <Link className="wordmark" to="/app">GRIDLOCK</Link>
      </header>
      <main className="container narrow">
        <h1>New league</h1>
        <form className="card form" onSubmit={submit}>
          <fieldset className="fs">
            <legend>Which football</legend>
            <div className="uni-pick">
              <button
                type="button"
                className={`uni ${universe === 'nfl' ? 'on' : ''}`}
                onClick={() => { setUniverse('nfl'); setEndWeek(18) }}
              >
                <b>NFL</b>
                <small>32 teams · 192 rooms</small>
              </button>
              <button
                type="button"
                className={`uni ${universe === 'fbs' ? 'on' : ''}`}
                onClick={() => {
                  setUniverse('fbs')
                  // An 18-week season is an NFL season. College boards are a
                  // third the size — 18 weeks x 6 slots is 108 spends, which
                  // is MORE rooms than the SEC has, so leaving the NFL default
                  // in place would get every college league refused at the
                  // moment of creation. Twelve weeks puts the big conferences
                  // at 0.67-0.75, right where the pro game sits.
                  if (endWeek > 12) setEndWeek(12)
                }}
                disabled={confs.length === 0}
              >
                <b>College</b>
                <small>
                  {confs.length === 0
                    ? 'not loaded yet'
                    : `${confs.length} conferences`}
                </small>
              </button>
            </div>

            {universe === 'fbs' && (
              <label>
                Conference
                <select value={conference} onChange={(e) => setConference(e.target.value)}>
                  {confs.map((c) => (
                    <option key={c.conference} value={c.conference}>
                      {c.conference} — {c.teams} teams, {c.rooms} rooms
                    </option>
                  ))}
                </select>
              </label>
            )}

            {pressure !== null && (
              <p className={`pressure ${pressure > 1 ? 'is-bad' : pressure > 0.85 ? 'is-tight' : ''}`}>
                <b>
                  {pressure > 1 ? 'Not playable.'
                    : pressure > 0.85 ? 'Very tight.'
                    : pressure > 0.45 ? 'Good pressure.'
                    : 'Roomy.'}
                </b>{' '}
                {slotCount * weekCount} spends against {rooms} rooms
                {' '}({Math.round(pressure * 100)}% of the board).{' '}
                {pressure > 1
                  ? 'The season needs more rooms than exist — shorten it or drop a roster slot.'
                  : pressure > 0.85
                    ? 'You will be scraping the barrel by the last few weeks.'
                    : pressure > 0.45
                      ? 'The board runs dry near the end, which is the whole game.'
                      : 'The burn will barely bite — consider a longer season.'}
              </p>
            )}
          </fieldset>

          <label>
            League name
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>

          <label>
            Your team name
            <input value={teamName} onChange={(e) => setTeamName(e.target.value)} />
          </label>

          <label>
            Roster
            <select value={preset} onChange={(e) => setPreset(Number(e.target.value))}>
              {ROSTER_PRESETS.map((p, i) => (
                <option key={p.label} value={i}>{p.label}</option>
              ))}
            </select>
          </label>

          <fieldset className="window">
            <legend>Season</legend>
            <div className="window-row">
              <label>
                Starts week
                <select value={startWeek}
                        onChange={(e) => {
                          const v = Number(e.target.value)
                          setStartWeek(v)
                          if (v > endWeek) setEndWeek(18)
                        }}>
                  {Array.from({ length: 18 }, (_, i) => i + 1).map((w) => (
                    <option key={w} value={w}>Week {w}</option>
                  ))}
                </select>
              </label>
              <label>
                Ends week
                <select value={endWeek} onChange={(e) => setEndWeek(Number(e.target.value))}>
                  {Array.from({ length: 18 }, (_, i) => i + 1)
                    .filter((w) => w >= startWeek)
                    .map((w) => (
                      <option key={w} value={w}>Week {w}</option>
                    ))}
                </select>
              </label>
            </div>
            {(() => {
              const q = seasonQuality(endWeek - startWeek + 1)
              return (
                <p className={`window-note is-${q.tone}`}>
                  <strong>{q.label}.</strong> {q.note}
                </p>
              )
            })()}
            <label>
              Close joins after
              <select
                value={joinDeadline}
                onChange={(e) =>
                  setJoinDeadline(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <option value="">Never — anyone can join any week</option>
                {Array.from({ length: 18 }, (_, i) => i + 1)
                  .filter((w) => w >= startWeek && w <= endWeek)
                  .map((w) => <option key={w} value={w}>Week {w}</option>)}
              </select>
            </label>
            <p className="window-fine">
              Someone joining late arrives with all 192 units unburned while everyone
              else has spent forty-something — they get the best room available every
              week for the rest of the year. Two or three weeks in is the usual cutoff.
            </p>

            <p className="window-fine">
              Playoffs run either way — the board wipes clean and everyone starts even.
            </p>
          </fieldset>

          <label>
            Scoring
            <select value={profileId} onChange={(e) => setProfileId(e.target.value)}>
              <option value="half_ppr">Half PPR</option>
              <option value="ppr">Full PPR</option>
              <option value="standard">Standard</option>
            </select>
          </label>

          <fieldset className="window">
            <legend>Who plays</legend>
            <label>
              League size
              <select value={maxMembers} onChange={(e) => setMaxMembers(Number(e.target.value))}>
                {[6, 8, 10, 12, 15, 20, 30].map((n) => (
                  <option key={n} value={n}>
                    {n} managers{n === 12 ? ' — recommended' : ''}
                  </option>
                ))}
              </select>
            </label>
            <p className="window-fine">
              Two managers submitting the same six rooms runs about 1.8% a week at
              twelve, and 11% at thirty. A dozen keeps a tie rare enough to be a
              story rather than a rules problem.
            </p>

            <label className="check">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => {
                  setIsPublic(e.target.checked)
                  if (e.target.checked && maxMembers > 15) setMaxMembers(15)
                }}
              />
              Open to anyone — list it publicly
            </label>
            <p className="window-fine">
              {isPublic
                ? 'Anyone signed in can find and join this league. Public leagues cap at 15.'
                : 'Private. Only people you send the join code to can get in.'}
            </p>
          </fieldset>

          <label className="check">
            <input
              type="checkbox"
              checked={hidePicks}
              onChange={(e) => setHidePicks(e.target.checked)}
            />
            Hide everyone's picks until kickoff
          </label>

          <label className="check">
            <input
              type="checkbox"
              checked={playoffReset}
              onChange={(e) => setPlayoffReset(e.target.checked)}
            />
            Wipe the burn board clean for the playoffs
          </label>

          <button className="btn btn-primary full" disabled={busy}>
            {busy ? 'Creating…' : 'Create league'}
          </button>
          {err && <p className="error">{err}</p>}
        </form>
      </main>
    </div>
  )
}
